import { Button } from "../../components/core/basic/Button";
import SummaryCard from "../../components/core/blocks/SummaryCard";
import { ProgramContextProvider, useProgramContext } from "../../components/custom/programs/ProgramContext";
import ProgramsForm from "../../components/custom/programs/ProgramsForm";
import { PROGRAM_DEFAULTS } from "../../components/custom/programs/constants";
import useQueryParams from "../../hooks/useQueryParams";
import { Col, Container as Grid, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import api from "../../utils/api";
import { errorToast } from "../../utils/toasts";
import { toTitleCase } from "../../utils/formatText";
import { ENGINES } from "../../modules/programs/constants";
import { useSimpleCan } from "../../config/casl/casl";

const ProgramCreate = () => {
  const { t } = useTranslation(["programs", "common"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const { engine } = useProgramContext();
  const location = useLocation();
  const cloneMode = useQueryParams("clone", "");

  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");

  const isReadOnly =
    (["bidding", "budget"].includes(engine) && !userCanUpdateAds) ||
    (engine === "lineitem_bidding" && !userCanUpdateDSPEntity);

  const defaultValues =
    cloneMode ? { ...PROGRAM_DEFAULTS[engine], ...location.state?.defaults } : PROGRAM_DEFAULTS[engine];
  const { handleSubmit, ...form } = useForm({
    shouldUnregister: false,
    /* prettier-ignore */
    defaultValues,
    mode: "onChange",
  });

  const navigateAfterCreate = () => {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "programs",
    });
    if (engine === "pricing") {
      navigate({ to: "/user/programs" });
      return;
    }
    const rulesSubTab = engine === "budget" ? "campaigns" : engine;
    navigate({ to: "/user/advertising/settings", search: { rulesSubTab } });
  };

  const createProgram = useMutation({
    mutationFn: async (data) => {
      const { name, start_date, end_date, ...rawSpec } = data;
      await api.post("/programs/", {
        name,
        start_date,
        end_date,
        spec: rawSpec,
        status: "scheduled",
        engine: engine ?? "pricing",
      });
    },
    onSuccess: navigateAfterCreate,
  });

  const isPending = createProgram.isPending || form.formState.isSubmitting;
  const engineTitleSelector = ENGINES?.[engine]?.title;
  const title =
    engineTitleSelector ? t(engineTitleSelector) : t(($) => $.programs["{name} Rules"], { name: toTitleCase(engine) });

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      <Grid fluid style={{ padding: "3rem", minHeight: "calc(100vh - 150px)" }}>
        <SummaryCard title={t(($) => $.programs["New {title}"], { title })}>
          <Row>
            <Col xs={12} lg={8} className="m-auto">
              <ProgramsForm {...form} isReadOnly={isReadOnly} />

              <Row className="mt-4">
                <Col md={12} className="d-flex justify-content-end">
                  <div style={{ marginRight: "2rem" }}>
                    <Button
                      variant="secondary"
                      onPress={() => {
                        router.history.back();
                      }}
                    >
                      {t(($) => $.common.actions.Cancel)}
                    </Button>
                  </div>
                  {!isReadOnly && (
                    <div>
                      <Button
                        isPending={isPending}
                        onPress={handleSubmit(
                          (data) => createProgram.mutate(data),
                          () => errorToast({ message: t(($) => $.programs["Some fields are missing or invalid."]) }),
                        )}
                      >
                        {t(($) => $.programs.footer.Create)}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </SummaryCard>
      </Grid>
    </FormProvider>
  );
};

const ProgramCreateView = () => {
  const [engine] = useQueryParams("engine", "pricing");
  return (
    <ProgramContextProvider engine={engine}>
      <ProgramCreate />
    </ProgramContextProvider>
  );
};

export default ProgramCreateView;
